import * as React from 'react'
import { useState, useRef, useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Drawer } from '@/components/primitives/Drawer'
import { Button } from '@/components/primitives/Button'
import { useToast } from '@/components/feedback/ToastProvider'
import { bulkImportResidents, type BulkImportRow } from './api'
import { listFacilities } from '@/features/facilities/api'
import { ApiError } from '@/lib/api'
import { useAuth } from '@/features/auth/AuthContext'
import { facilityFilterStore } from '@/store/facilityFilterStore'
import './ImportCsvDrawer.css'

interface Props {
  open: boolean
  onClose: () => void
}

// ── CSV columns ──────────────────────────────────────────────────────────────
const REQUIRED_COLS = ['first_name', 'last_name'] as const
const OPTIONAL_COLS = ['mrn', 'dob', 'room', 'sex', 'phone', 'pcp_name', 'pcp_fax'] as const
const ALL_COLS = [...REQUIRED_COLS, ...OPTIONAL_COLS]

// ── Simple CSV parser ────────────────────────────────────────────────────────
function splitRow(line: string): string[] {
  const result: string[] = []
  let inQuotes = false
  let current = ''
  for (const ch of line) {
    if (ch === '"') {
      inQuotes = !inQuotes
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim())
      current = ''
    } else {
      current += ch
    }
  }
  result.push(current.trim())
  return result
}

function parseCSV(text: string): { headers: string[]; rows: Record<string, string>[] } | null {
  const lines = text.trim().split(/\r?\n/).filter(Boolean)
  if (lines.length < 2) return null
  const headers = splitRow(lines[0] ?? '').map((h) => h.toLowerCase())
  const rows = lines.slice(1).map((line) => {
    const vals = splitRow(line)
    return Object.fromEntries(headers.map((h, i) => [h, vals[i] ?? ''] as [string, string]))
  })
  return { headers, rows }
}

function downloadTemplate(): void {
  const header = ALL_COLS.join(',')
  const sample = [
    'Jane,Smith,MRN-1006,1943-09-12,204A,female,(509) 555-4112,Dr. Karen Whitfield,(312) 555-0100',
    'John,Doe,MRN-1007,1939-02-25,108B,male,,Dr. Alan Reed,(312) 555-0144',
  ].join('\n')
  const csv = `${header}\n${sample}\n`
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'residents_template.csv'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function rowToImportRow(r: Record<string, string>): BulkImportRow {
  return {
    first_name: r['first_name'] ?? '',
    last_name: r['last_name'] ?? '',
    mrn: r['mrn'] || null,
    dob: r['dob'] || null,
    room: r['room'] || null,
    sex: (r['sex'] as BulkImportRow['sex']) || null,
    phone: r['phone'] || null,
    pcp_name: r['pcp_name'] || null,
    pcp_fax: r['pcp_fax'] || null,
  }
}

type Stage = 'idle' | 'preview'

export function ImportCsvDrawer({ open, onClose }: Props): React.JSX.Element {
  const { user } = useAuth()
  const qc = useQueryClient()
  const toast = useToast()
  const fileRef = useRef<HTMLInputElement>(null)
  const [stage, setStage] = useState<Stage>('idle')
  const [pasteText, setPasteText] = useState('')
  const [headers, setHeaders] = useState<string[]>([])
  const [rows, setRows] = useState<Record<string, string>[]>([])
  const [dragOver, setDragOver] = useState(false)

  // SUPER_ADMIN has no facility_id — let them pick a facility.
  const needsFacilityPick = !user?.facility_id
  // Pre-select whatever the SUPER_ADMIN has chosen in the ManageBar switcher.
  const [selectedFacilityId, setSelectedFacilityId] = useState(
    () => facilityFilterStore.getSnapshot().facilityId ?? '',
  )
  // Sync switcher selection each time the drawer opens.
  React.useEffect(() => {
    if (open && needsFacilityPick) {
      setSelectedFacilityId(facilityFilterStore.getSnapshot().facilityId ?? '')
    }
  }, [open, needsFacilityPick])

  const facilitiesQuery = useQuery({
    queryKey: ['facilities'],
    queryFn: listFacilities,
    enabled: needsFacilityPick,
  })
  const facilities = facilitiesQuery.data?.filter((f) => f.status === 'active') ?? []

  const targetFacilityId = needsFacilityPick ? selectedFacilityId : (user?.facility_id ?? '')

  const missingRequired = REQUIRED_COLS.filter((c) => !headers.includes(c))
  const canImport =
    stage === 'preview' &&
    rows.length > 0 &&
    missingRequired.length === 0 &&
    !!targetFacilityId

  const mutation = useMutation({
    mutationFn: (importRows: BulkImportRow[]) => {
      if (!targetFacilityId) throw new Error('Select a facility first.')
      return bulkImportResidents(targetFacilityId, importRows)
    },
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['residents'] })
      const imported = res.created
      const failed = res.errors.length
      if (imported > 0) {
        const msg = failed > 0
          ? `${imported} resident${imported !== 1 ? 's' : ''} imported, ${failed} row${failed !== 1 ? 's' : ''} failed.`
          : `${imported} resident${imported !== 1 ? 's' : ''} imported successfully.`
        toast.success('Import complete', msg)
      } else {
        toast.error('Import failed', `All ${failed} row${failed !== 1 ? 's' : ''} failed to import.`)
      }
      handleClose()
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Import failed. Try again.'
      toast.error('Import failed', msg)
    },
  })

  function loadText(text: string): void {
    const parsed = parseCSV(text)
    if (!parsed || parsed.rows.length === 0) {
      toast.warning('Nothing to import', 'The file appears to be empty or has only one row.')
      return
    }
    setHeaders(parsed.headers)
    setRows(parsed.rows)
    setStage('preview')
  }

  function handleFile(file: File): void {
    const reader = new FileReader()
    reader.onload = (e) => loadText((e.target?.result as string) ?? '')
    reader.readAsText(file)
  }

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }, [])

  function reset(): void {
    setStage('idle')
    setPasteText('')
    setHeaders([])
    setRows([])
    setSelectedFacilityId(facilityFilterStore.getSnapshot().facilityId ?? '')
    if (fileRef.current) fileRef.current.value = ''
  }

  function handleClose(): void {
    reset()
    onClose()
  }

  // ── Idle stage ─────────────────────────────────────────────────────────────
  const idleContent = (
    <div className="import-idle">
      <div className="import-template-row">
        <div>
          <p className="import-template-title">Need the format?</p>
          <p className="import-template-sub muted small">
            Download a ready-made CSV template with all the columns and a sample row.
          </p>
        </div>
        <button type="button" className="import-template-btn" onClick={downloadTemplate}>
          <DownloadIcon />
          Download template
        </button>
      </div>
      {needsFacilityPick && (
        <div className="import-facility-pick">
          <label className="import-facility-label" htmlFor="import-facility">
            Import into facility
          </label>
          <select
            id="import-facility"
            className="select"
            value={selectedFacilityId}
            onChange={(e) => setSelectedFacilityId(e.target.value)}
          >
            <option value="">— Select a facility —</option>
            {facilities.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </div>
      )}
      <div
        className={'import-dropzone' + (dragOver ? ' import-dropzone-over' : '')}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => fileRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && fileRef.current?.click()}
      >
        <UploadIcon />
        <p className="import-dropzone-title">Drop a CSV file here</p>
        <p className="import-dropzone-sub muted small">or click to browse</p>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
        />
      </div>

      <div className="import-or">
        <span className="import-or-line" />
        <span className="import-or-text muted small">or paste CSV</span>
        <span className="import-or-line" />
      </div>

      <textarea
        className="import-paste"
        rows={6}
        placeholder={'first_name,last_name,mrn,dob,room,sex,phone,pcp_name,pcp_fax\nJane,Smith,MRN-001,1943-09-12,101A,female,,Dr. White,'}
        value={pasteText}
        onChange={(e) => setPasteText(e.target.value)}
      />

      <div className="import-columns muted small">
        <strong>Required:</strong>{' '}
        <span className="mono">{REQUIRED_COLS.join(', ')}</span>
        {'  ·  '}
        <strong>Optional:</strong>{' '}
        <span className="mono">{OPTIONAL_COLS.join(', ')}</span>
      </div>
    </div>
  )

  // ── Preview stage ──────────────────────────────────────────────────────────
  const PREVIEW_COLS = ALL_COLS.filter((c) => headers.includes(c))
  const previewRows = rows.slice(0, 5)

  const previewContent = (
    <div className="import-preview">
      <div className="import-summary">
        <span className="import-badge">{rows.length} row{rows.length !== 1 ? 's' : ''} ready</span>
        {missingRequired.length > 0 ? (
          <span className="import-badge import-badge-error">
            Missing: {missingRequired.join(', ')}
          </span>
        ) : (
          <span className="import-badge import-badge-ok">Columns OK</span>
        )}
        <button type="button" className="import-change-link muted small" onClick={reset}>
          Change file
        </button>
      </div>

      {needsFacilityPick && (
        <div className="import-facility-pick">
          <label className="import-facility-label" htmlFor="import-facility-preview">
            Import into facility
          </label>
          <select
            id="import-facility-preview"
            className="select"
            value={selectedFacilityId}
            onChange={(e) => setSelectedFacilityId(e.target.value)}
          >
            <option value="">— Select a facility —</option>
            {facilities.map((f) => (
              <option key={f.id} value={f.id}>{f.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="import-table-wrap">
        <table className="import-table">
          <thead>
            <tr>
              {PREVIEW_COLS.map((c) => (
                <th key={c} className="mono">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {previewRows.map((row, i) => (
              <tr key={i}>
                {PREVIEW_COLS.map((c) => (
                  <td key={c}>{row[c] || <span className="muted">—</span>}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 5 ? (
          <p className="import-more muted small">… and {rows.length - 5} more row{rows.length - 5 !== 1 ? 's' : ''}</p>
        ) : null}
      </div>
    </div>
  )

  const footerContent = (
    <>
      <Button variant="ghost" onClick={handleClose} type="button">
        Cancel
      </Button>
      {stage === 'idle' && pasteText.trim() ? (
        <Button type="button" onClick={() => loadText(pasteText)}>
          Preview
        </Button>
      ) : null}
      {stage === 'preview' ? (
        <Button
          type="button"
          disabled={!canImport}
          loading={mutation.isPending}
          onClick={() => mutation.mutate(rows.map(rowToImportRow))}
        >
          Import {rows.length} resident{rows.length !== 1 ? 's' : ''}
        </Button>
      ) : null}
    </>
  )

  return (
    <Drawer
      open={open}
      onClose={handleClose}
      size="lg"
      title="Import residents"
      subtitle="Bulk-add residents from a CSV file or pasted data."
      footer={footerContent}
    >
      {stage === 'idle' && idleContent}
      {stage === 'preview' && previewContent}
    </Drawer>
  )
}

function DownloadIcon(): React.JSX.Element {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}

function UploadIcon(): React.JSX.Element {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="16 16 12 12 8 16" />
      <line x1="12" y1="12" x2="12" y2="21" />
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
    </svg>
  )
}
